import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import InboxIcon from "@mui/icons-material/MoveToInbox";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MailIcon from "@mui/icons-material/Mail";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Avatar from "@mui/material/Avatar";

import Logo from "images/valoo-logo.svg";
import InfoIcon from "@mui/icons-material/Info";
import { styled, alpha } from "@mui/material/styles";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Collapse,
  Stack,
  Typography,
  useTheme,
  Badge,
  CircularProgress,
} from "@mui/material";
import {
  Assignment,
  Business,
  BusinessCenter,
  Dashboard,
  ExpandLess,
  ExpandMore,
  Group,
  Inventory,
  LogoutTwoTone,
  ManageAccounts,
  Poll,
  Pool,
  QuestionAnswer,
  Sell,
  ShowChart,
  Update,
  Wallet,
} from "@mui/icons-material";
import { useAuth0 } from "@auth0/auth0-react";
import { AuthenticationGuard } from "components/providers/AuthGuard";
import { useAuth } from "hooks/useAuth";
import NotificationMenu from "components/admin/Notification/NotificationMenu";

const drawerWidth = 280;

// Simplified styled components
const StyledDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    width: drawerWidth,
    boxSizing: "border-box",
    backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
    boxShadow: "0 0 10px rgba(0, 0, 0, 0.05)",
    borderRight: "none",
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  margin: "2px 8px",
  borderRadius: "8px",
  padding: "8px 16px",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  "&.active": {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
    "& .MuiListItemIcon-root": {
      color: theme.palette.primary.main,
    },
    "&::before": {
      content: '""',
      position: "absolute",
      left: "-8px",
      top: "50%",
      transform: "translateY(-50%)",
      height: "60%",
      width: "4px",
      backgroundColor: theme.palette.primary.main,
      borderRadius: "0 4px 4px 0",
    },
  },
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: "40px",
  color:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.7)
      : alpha(theme.palette.common.black, 0.6),
}));

const UserInfoContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const dashboardRoutes = [
  {
    label: "Dashboard",
    path: "/admin",
    Icon: Dashboard,
  },
  {
    label: "Users",
    path: "/admin/manage-users",
    Icon: ManageAccounts,
    children: [
      {
        label: "All Users",
        path: "/admin/manage-users",
        Icon: Group,
      },
      {
        label: "User Wallet Buy",
        path: "/admin/wallet/buy",
        Icon: Inventory,
      },
      {
        label: "User Wallet Sell",
        path: "/admin/wallet/sell",
        Icon: Sell,
      },
    ],
  },
  {
    label: "Companies",
    path: "/admin/manage-companies",
    Icon: Business,
    children: [
      {
        label: "All Companies",
        path: "/admin/manage-companies",
        Icon: BusinessCenter,
      },
      {
        label: "Company Stocks",
        path: "/admin/manage-company-stocks",
        Icon: ShowChart,
      },
      {
        label: "Company Wallet",
        path: "/admin/manage-company-wallet",
        Icon: Wallet,
      },
    ],
  },

  {
    label: "Updates",
    path: "/admin/manage-stay-updated",
    Icon: Update,
    children: [
      {
        label: "Stay Updated",
        path: "/admin/manage-stay-updated",
        Icon: Update,
      },
      {
        label: "NewsLetters",
        path: "/admin/manage-newsletters",
        Icon: MailIcon,
      },
      {
        label: "ShareHolder Updates",
        path: "/admin/manage-shareholders",
        Icon: InboxIcon,
      },
    ],
  },
  {
    label: "User CompanyNDA",
    path: "/admin/user-company-nda",
    Icon: Assignment,
  },
  {
    label: "Questionnaires",
    path: "/admin/questioner",
    Icon: Poll,
    children: [
      { label: "Q's List", path: "/admin/questioner", Icon: Poll },
      {
        label: "Q's Responses",
        path: "/admin/questioner-answer",
        Icon: QuestionAnswer,
      },
      {
        label: "Confirmation Settings",
        path: "/admin/questioner-post-registration",
        Icon: Assignment,
      },
    ],
  },

  {
    label: "Registration Forms",
    path: "/admin/registration-survey",
    Icon: Pool,
  },
  {
    label: "Field Details",
    path: "/admin/info",
    Icon: InfoIcon,
  },
];

const UserMenu = ({ user }) => {
  const { logout, logoutLoading } = useAuth();

  const handleLogout = async () => {
    await logout();
  };
  return (
    <UserInfoContainer>
      <Avatar sx={{ backgroundColor: "primary.main" }} alt="John">
        {useAuth0().user?.email?.charAt(0)}
      </Avatar>
      <Box sx={{ ml: 2, flexGrow: 1 }}>
        <Typography variant="subtitle2" fontWeight="bold">
          Admin
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {user?.email}
        </Typography>
      </Box>
      <IconButton
        disabled={logoutLoading}
        onClick={handleLogout}
        size="small"
        edge="end"
      >
        {logoutLoading ? (
          <CircularProgress size={20} />
        ) : (
          <LogoutTwoTone fontSize="small" />
        )}
      </IconButton>
    </UserInfoContainer>
  );
};

const SideBarLink = ({
  route,
  children,
  sx,
  isActive,
  hasBadge,
  badgeContent,
  ...rest
}) => {
  return (
    <ListItem sx={{ py: 0 }}>
      <StyledListItemButton
        className={isActive ? "active" : ""}
        {...(route?.children?.length
          ? {}
          : {
              LinkComponent: NavLink,
              to: route.path,
            })}
        end
        {...rest}
        disableRipple
        sx={{
          ...sx,
        }}
      >
        {children}
        {hasBadge && (
          <Badge color="error" badgeContent={badgeContent} sx={{ ml: 1 }} />
        )}
      </StyledListItemButton>
    </ListItem>
  );
};

function AdminDashboardLayout() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const { user } = useAuth0();

  const [expandState, setExpandState] = React.useState({});
  const { pathname } = useLocation();
  const theme = useTheme();

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <LogoContainer component={Link} to="/admin">
        <img
          src={Logo || "/placeholder.svg"}
          alt="logo"
          style={{ maxWidth: "70%", height: "auto" }}
        />
      </LogoContainer>

      <Divider />

      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        <List>
          {dashboardRoutes.map((route) => (
            <React.Fragment key={route.path}>
              <SideBarLink
                route={route}
                onClick={(e) => {
                  if (route?.children?.length) {
                    e.preventDefault();
                    e.stopPropagation();
                    setExpandState({
                      ...expandState,
                      [route.path]: !expandState[route.path],
                    });
                  }
                }}
                isActive={
                  (pathname === route.path && !route.children?.length) ||
                  (route.children?.length &&
                    route.children.some((child) =>
                      pathname.includes(child.path)
                    ))
                }
                hasBadge={route.badge !== undefined}
                badgeContent={route.badge}
                end
              >
                <StyledListItemIcon>
                  <route.Icon />
                </StyledListItemIcon>
                <ListItemText
                  primary={route.label}
                  primaryTypographyProps={{
                    fontWeight: pathname.includes(route.path) ? 600 : 400,
                  }}
                />
                {route?.children?.length ? (
                  <>
                    {expandState[route.path] ? (
                      <ExpandLess fontSize="small" />
                    ) : (
                      <ExpandMore fontSize="small" />
                    )}
                  </>
                ) : null}
              </SideBarLink>
              <Collapse in={Boolean(expandState[route.path])} timeout="auto">
                <List
                  sx={{
                    pl: 2,
                  }}
                  component="div"
                  disablePadding
                >
                  {route?.children?.map((child) => (
                    <SideBarLink
                      key={child.path}
                      route={child}
                      isActive={pathname === child.path}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <ListItemText
                        primary={child.label}
                        slotProps={{
                          primary: {
                            sx: { fontSize: "0.95rem" },
                          },
                        }}
                      />
                    </SideBarLink>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          ))}
        </List>
      </Box>

      <UserMenu user={user} />
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            color="primary"
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}></Box>
          <Stack direction={"row"} gap={2} alignItems={"center"}>
            <NotificationMenu />
          </Stack>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <StyledDrawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
          }}
          open
        >
          {drawer}
        </StyledDrawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}

const Protected = () => {
  return <AuthenticationGuard component={AdminDashboardLayout} />;
};

export default Protected;
